import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/react';
import posthog from 'posthog-js';
import {
  ANALYTICS_CONSENT_UPDATED_EVENT,
  isAnalyticsConsentGranted,
  isAppPageviewCaptureAllowed,
  readConsentStatus,
  syncAnalyticsConsentFromCookie,
} from './consent';
import { getAppPageContext, initAppIntentAnalytics, registerAppPageContext } from './intent-analytics';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://eu.i.posthog.com';

function registerAppSurface(): void {
  posthog.register(getAppPageContext(window.location.pathname));
}

/** Initialize PostHog. Call once at app startup. */
export function initPostHog() {
  if (!POSTHOG_KEY) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    persistence: 'cookie',
    cross_subdomain_cookie: true,
    cookieless_mode: 'on_reject',
    opt_out_capturing_persistence_type: 'cookie',
    defaults: '2026-01-30',
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug();
    },
  });

  syncAnalyticsConsentFromCookie();
  registerAppSurface();
}

/** Call after Clerk auth to link PostHog to user. */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!isAnalyticsConsentGranted()) return;
  posthog.identify(userId, properties);
}

/** Call on sign-out. */
export function resetUser() {
  posthog.reset();
  const consentStatus = readConsentStatus();
  if (consentStatus === 'granted') {
    posthog.opt_in_capturing();
  } else if (consentStatus === 'denied') {
    posthog.opt_out_capturing();
  }
  registerAppSurface();
}

/** Track a business event. */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
  options?: { sendInstantly?: boolean },
) {
  if (!isAnalyticsConsentGranted()) return;

  if (options?.sendInstantly) {
    posthog.capture(event, properties, { send_instantly: true });
  } else {
    posthog.capture(event, properties);
  }
}

/** Set person properties on the current PostHog user. */
export function setUserProperties(properties: Record<string, unknown>) {
  if (!isAnalyticsConsentGranted()) return;
  posthog.setPersonProperties(properties);
}

/** Syncs Clerk auth state with PostHog user identity. */
export function PostHogIdentifier() {
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    const syncIdentity = () => {
      if (isSignedIn && user && isAnalyticsConsentGranted()) {
        identifyUser(user.id, {
          email: user.primaryEmailAddress?.emailAddress,
          name: user.fullName,
          signup_date: user.createdAt?.toISOString(),
        });
      } else if (!isSignedIn) {
        resetUser();
      }
    };

    syncIdentity();
    window.addEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, syncIdentity);

    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, syncIdentity);
    };
  }, [isSignedIn, user]);

  return null;
}

/** Captures $pageview on SPA route changes. Must be inside BrowserRouter. */
export function PostHogPageTracker() {
  const location = useLocation();

  useEffect(() => {
    const capturePageview = () => {
      if (!isAppPageviewCaptureAllowed()) return;

      const pageContext = registerAppPageContext(location.pathname);
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        ...pageContext,
        page_path: location.pathname,
        page_search: location.search,
      });
    };

    capturePageview();
    window.addEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, capturePageview);

    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, capturePageview);
    };
  }, [location.pathname, location.search]);

  return null;
}

export function PostHogIntentTracker() {
  useEffect(() => {
    initAppIntentAnalytics();
  }, []);

  return null;
}

export { posthog };
